import React, { useState, useRef, useEffect } from "react";
import { TABLE } from "@/utils/constant/table.constant";
import Button from "@/common/ui/Button";
import { H2, LightText2 } from "@/common/ui/Text";
import { FiSearch } from "react-icons/fi";
import Checkbox from "@/common/custom/Chekbox";
import { DragIcon, pinSelected } from "@/dmr/common/SvgIcons";
import { toastMessage } from "@/utils/helper/myToast";
import { addConfig, getOrgConfig } from "@/services/employee.services";
import { useAppDispatch } from "@/store/store";
import { employeeSelector, setTableConfig, setPinned } from "@/store/features/employeeSlice";
import { useSelector } from "react-redux";
import { GrFormPin } from "react-icons/gr";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light.css";
import ManageColumnSkeleton from "@/common/ui/ManageColumnSkeleton";
import { loginSelector } from "@/store/features/loginSlice";
import { PINNED_LOCALSTORAGE_KEY } from "@/utils/constant/global.constant";
import { useFormChangeWarning } from "@/hooks/useFormChangeWarning";

function arrayMove(arr: any, from: any, to: any) {
  const newArr = [...arr];
  const item = newArr.splice(from, 1)[0];
  newArr.splice(to, 0, item);
  return newArr;
}

interface ManageColumnProps {
  onClose: (props?: any) => void;
}

const ManageColumn: React.FC<ManageColumnProps> = ({ onClose }) => {
  const { tableConfig, pinned } = useSelector(employeeSelector);
  const { currentOrg } = useSelector(loginSelector);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const initialVisibleFields = tableConfig?.visibleFields
    ? tableConfig.visibleFields.map((id) => TABLE.EMPLOYEE_TABLE_FIELDS.find((f) => f.id === id)).filter((f): f is NonNullable<typeof f> => f !== undefined)
    : TABLE.EMPLOYEE_TABLE_FIELDS;

  const initialHiddenFields = tableConfig?.hiddenFields
    ? tableConfig.hiddenFields.map((id) => TABLE.EMPLOYEE_TABLE_FIELDS.find((f) => f.id === id)).filter((f): f is NonNullable<typeof f> => f !== undefined)
    : [];

  const [visibleFields, setVisibleFields] = useState(initialVisibleFields);
  const [hiddenFields, setHiddenFields] = useState(initialHiddenFields);

  // Track if there are changes
  const isFieldsChanged =
    JSON.stringify(visibleFields.map((f) => f.id)) !== JSON.stringify(initialVisibleFields.map((f) => f.id)) ||
    JSON.stringify(hiddenFields.map((f) => f.id)) !== JSON.stringify(initialHiddenFields.map((f) => f.id));
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState("");
  const dragItem: any = useRef<number | null>(null);
  const dragOverItem: any = useRef<number | null>(null);
  const dispatch = useAppDispatch();

  const handleToggle = (field: any, isVisible: boolean) => {
    if (isVisible) {
      setVisibleFields(visibleFields.filter((f) => f.id !== field.id));
      setHiddenFields([...hiddenFields, field]);
    } else {
      setHiddenFields(hiddenFields.filter((f) => f.id !== field.id));
      setVisibleFields([...visibleFields, field]);
    }
  };

  const filterFields = (fields: any[]) => {
    if (!search.trim()) return fields;
    return fields.filter((field) => field.label.toLowerCase().includes(search.toLowerCase()));
  };

  const handleSave = async () => {
    setIsLoading("save");
    try {
      const payload = {
        visibleFields: visibleFields.map((f) => f.id),
        hiddenFields: hiddenFields.map((f) => f.id),
      };
      const res: any = await addConfig("emp_tbl", payload);
      if (res?.status === 200) {
        const { message, isError } = res.data;
        onClose({ disabledWarning: true });
        dispatch(setTableConfig(payload));
        return toastMessage(message, isError);
      } else {
        console.log("Error", res);
        const { message, isError } = res?.response?.data || {};
        if (message) {
          return toastMessage(message, isError);
        }
        if (res.message) {
          return toastMessage(res.message, true);
        }
      }
    } catch (error) {
      console.error("Error saving configuration:", error);
      toastMessage("There was an error in applying the changes, please try again!", true);
    } finally {
      setIsLoading("");
    }
  };

  const handleReset = async () => {
    // Default: first 7 fields visible, rest hidden, no pinned
    const defaultVisible = TABLE.EMPLOYEE_TABLE_FIELDS;
    setVisibleFields(defaultVisible);
    setHiddenFields([]);
    setIsLoading("reset");
    try {
      const payload = {
        visibleFields: defaultVisible.map((f) => f.id),
        hiddenFields: [],
      };
      const res: any = await addConfig("emp_tbl", payload);
      if (res?.status === 200) {
        dispatch(setTableConfig(payload));
        const { message, isError } = res?.data || {};
        toastMessage(message, isError);
        onClose({ disabledWarning: true });
      } else {
        toastMessage("There was an error in applying the changes, please try again!", true);
      }
    } catch (e) {
      toastMessage("There was an error in applying the changes, please try again!", true);
    } finally {
      setIsLoading("");
    }
  };

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setIsInitialLoading(true);
        const res: any = await getOrgConfig("emp_tbl");
        if (res?.status === 200 && res.data) {
          dispatch(setTableConfig(res.data?.data));
        }
      } catch (error) {
        console.error("Failed to fetch table config:", error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadConfig();
  }, []);
  useFormChangeWarning(isFieldsChanged);

  // Reorder columns based on pinned state
  useEffect(() => {
    // Find default pinned columns (isDefaultPinned: true)
    const defaultPinnedFields = visibleFields.filter((f) => f.isDefaultPinned);
    // Remove them from the rest
    let restFields = visibleFields.filter((f) => !f.isDefaultPinned);

    // If user-pinned column exists, move it to 3rd position (after default pinned columns)
    if (pinned && pinned.length > 0) {
      const pinnedField = restFields.find((f) => f.id === pinned[0]);
      if (pinnedField) {
        restFields = [pinnedField, ...restFields.filter((f) => f.id !== pinned[0])];
      }
    }

    // New order: default pinned (1st, 2nd) + user pinned (3rd) + rest
    const reorderedFields = [...defaultPinnedFields, ...restFields];

    // Only update if the order actually changed
    if (JSON.stringify(reorderedFields.map((f) => f.id)) !== JSON.stringify(visibleFields.map((f) => f.id))) {
      setVisibleFields(reorderedFields);
    }
  }, [pinned]);

  // Show skeleton loader while fetching initial config
  if (isInitialLoading) {
    return <ManageColumnSkeleton />;
  }

  return (
    <div className="bg-white rounded-lg px-4 py-2 h-[calc(100%-60px)] flex flex-col gap-2 justify-between max-w-full">
      <div className="relative">
        <input
          type="text"
          placeholder="Search Fields"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`px-3 py-7 pr-8 max-h-8 rounded-lg border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500 cursor-pointer flex justify-between items-center transition-all w-full duration-100 ease-in-out text-nowrap !text-13 outline-none placeholder:text-gray-400 text-text-default`}
        />
        {search ? (
          <button
            type="button"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg"
            onClick={() => setSearch("")}
            aria-label="Clear search"
          >
            ×
          </button>
        ) : (
          <FiSearch className="text-icon-default2 absolute right-4 top-1/2 -translate-y-1/2" />
        )}
      </div>
      <div className=" flex h-full flex-col gap-4 max-h-[calc(100%-100px)] overflow-y-auto ">
        <div className=" pt-2">
          <H2 text="Visible columns" className="!text-13" />
          {filterFields(visibleFields).length === 0 ? (
            <div className="text-gray-400 text-sm py-2 text-center">No fields </div>
          ) : (
            filterFields(visibleFields).map((field, idx) => {
              const isPinned = pinned && pinned.length > 0 && field.id === pinned[0];
              const dragBlocked = field.isDraggable === false || isPinned;
              return (
                <div
                  key={field.id}
                  className={`flex items-center group py-1 ${dragBlocked ? "cursor-not-allowed" : "cursor-grab hover:bg-stroke-light"}`}
                  draggable={!dragBlocked}
                  onDragStart={dragBlocked ? undefined : () => (dragItem.current = idx)}
                  onDragEnter={dragBlocked ? undefined : () => (dragOverItem.current = idx)}
                  onDragEnd={
                    dragBlocked
                      ? undefined
                      : () => {
                          if (dragItem.current !== null && dragOverItem.current !== null && dragItem.current !== dragOverItem.current) {
                            const filtered = filterFields(visibleFields);
                            const from = visibleFields.findIndex((f) => f.id === filtered[dragItem.current].id);
                            const to = visibleFields.findIndex((f) => f.id === filtered[dragOverItem.current].id);
                            setVisibleFields(arrayMove(visibleFields, from, to));
                          }
                          dragItem.current = null;
                          dragOverItem.current = null;
                        }
                  }
                  onDragOver={dragBlocked ? undefined : (e) => e.preventDefault()}
                >
                  <span className={` text-gray-400 group-hover:text-gray-700 select-none ${dragBlocked ? "opacity-50" : ""}`} style={{ fontSize: 18 }}>
                    {DragIcon}
                  </span>
                  <Checkbox
                    checked={true}
                    className="!p-1"
                    checkBoxSize="w-3.5 h-3.5"
                    onChange={() => handleToggle(field, true)}
                    aria-label={field.label}
                    disabled={isPinned || field.isDraggable === false}
                    disabledClassName=""
                  />
                  <LightText2 text={field.label} className={isPinned ? "text-gray-400" : ""} />
                  {/* Pin icon handling */}
                  {field?.isDraggable !== false && (
                    <div className="ml-auto flex items-center px-2">
                      {isPinned ? (
                        <Tippy content="Click to unpin" theme="light" className=" !text-text-default !text-13">
                          <button
                            onClick={() => {
                              dispatch(setPinned([]));
                              localStorage.removeItem(`${currentOrg?.SignupOrg?.ext_org_id}${PINNED_LOCALSTORAGE_KEY}`);
                            }}
                            className="hover:bg-gray-100 p-1 rounded"
                          >
                            {pinSelected}
                          </button>
                        </Tippy>
                      ) : (
                        <Tippy
                          content={pinned.length > 0 ? "Maximum of 1 column can be pinned" : "Click to pin"}
                          theme="light"
                          className=" !text-text-default !text-13"
                        >
                          <span className="opacity-0 group-hover:opacity-100">
                            <button
                              onClick={() => {
                                if (pinned.length === 0) {
                                  dispatch(setPinned([field.id]));
                                }
                              }}
                              className={`hover:bg-gray-100 p-1 rounded ${pinned.length > 0 ? "cursor-not-allowed" : "cursor-pointer"}`}
                              disabled={pinned.length > 0}
                            >
                              <GrFormPin className={`h-4 w-4 ${pinned.length > 0 ? "text-gray-400" : "text-text-default hover:text-text-primary"}`} />
                            </button>
                          </span>
                        </Tippy>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        <div className="">
          <H2 text="Hidden columns" className="!text-13" />
          {filterFields(hiddenFields).length === 0 ? (
            <div className="text-gray-400 text-sm py-2 text-center">No fields </div>
          ) : (
            filterFields(hiddenFields).map((field) => (
              <div key={field.id} className="flex items-center">
                <Checkbox checked={false} className="" onChange={() => handleToggle(field, false)} aria-label={field.label} />
                <LightText2 text={field.label} />
              </div>
            ))
          )}
        </div>
      </div>
      <div className="flex justify-between gap-2">
        <Button variant="transparent" size="small" onClick={handleReset} isLoading={isLoading === "reset"} disabled={Boolean(isLoading)} className=" !px-0">
          Reset to default
        </Button>
        <div className=" flex gap-x-4">
          <Button variant="secondary" size="small" onClick={onClose} disabled={Boolean(isLoading)}>
            Cancel
          </Button>
          <Button variant="primary" size="small" onClick={handleSave} isLoading={isLoading === "save"} disabled={Boolean(isLoading) || !isFieldsChanged}>
            Save & Apply
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ManageColumn;
